import React, { createContext, FC, useContext, useEffect, useState } from "react";
import { BenchmarkingLineOfBusinessEnum } from "@app/overviews/network-benchmarking-dashboard/filters/shared";
import { useCompetitiveInsightsManageProjects } from "@app/overviews/network-benchmarking-dashboard/manage/hooks/useCompetitiveInsightsManageProjects";
import { CompetitiveInsightsManageDisplayFilters } from "@app/overviews/network-benchmarking-dashboard/manage/shared/CompetitiveInsightsManageDisplayFilters";
import { CompetitiveInsightsManageProjectStatusEnum } from "@app/overviews/network-benchmarking-dashboard/manage/shared/CompetitiveInsightsManageProjectStatusEnum";
import { CompetitiveInsightsSupersetProjectConfigurations } from "@app/overviews/network-benchmarking-dashboard/manage/shared/CompetitiveInsightsSupersetProjectConfigurations";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";
import { UserContext } from "@app/user/UserContext";

// the API can return the line of business as its lowercase name (e.g. "medicare") instead of
// the BenchmarkingLineOfBusinessEnum numeric value, so match case-insensitively against the enum's name keys
export const parseLineOfBusiness = (lineOfBusiness: BenchmarkingLineOfBusinessEnum | string): BenchmarkingLineOfBusinessEnum | undefined => {
  if (lineOfBusiness === null || lineOfBusiness === undefined) {
    return undefined;
  }

  if (typeof lineOfBusiness === "number") {
    return lineOfBusiness;
  }

  const matchedKey = Object.keys(BenchmarkingLineOfBusinessEnum).find(
    key => isNaN(Number(key)) && key.toLowerCase() === lineOfBusiness.toLowerCase()
  );

  return matchedKey ? BenchmarkingLineOfBusinessEnum[matchedKey as keyof typeof BenchmarkingLineOfBusinessEnum] : undefined;
};

export const getLineOfBusinessDisplayName = (lineOfBusiness: BenchmarkingLineOfBusinessEnum | string): string => {
  const parsedLineOfBusiness = parseLineOfBusiness(lineOfBusiness);
  return parsedLineOfBusiness !== undefined ? BenchmarkingLineOfBusinessEnum[parsedLineOfBusiness] : "";
};

interface CompetitiveInsightsManageContextValues {
  activeOptions: DropdownItem<CompetitiveInsightsManageProjectStatusEnum>[];
  clientOptions: DropdownItem<string>[];
  displayedFilters: CompetitiveInsightsManageDisplayFilters;
  filteredProjects: CompetitiveInsightsSupersetProjectConfigurations[];
  isQuestInternal: boolean;
  lineOfBusinessOptions: DropdownItem<BenchmarkingLineOfBusinessEnum>[];
  onApplyFiltersClick: () => void;
  onClearCompetitiveInsightsManageFiltersClick: () => void;
  projects: CompetitiveInsightsSupersetProjectConfigurations[];
  projectsLoading: boolean;
  refetchProjects: () => Promise<void>;
  selectedActive: DropdownItem<CompetitiveInsightsManageProjectStatusEnum>[];
  selectedClients: DropdownItem<string>[];
  selectedLinesOfBusiness: DropdownItem<BenchmarkingLineOfBusinessEnum>[];
  selectedStates: DropdownItem<string>[];
  setDisplayedFilters: (displayedFilters: CompetitiveInsightsManageDisplayFilters) => void;
  setSelectedActive: (selectedActive: DropdownItem<CompetitiveInsightsManageProjectStatusEnum>[]) => void;
  setSelectedClients: (selectedClients: DropdownItem<string>[]) => void;
  setSelectedLinesOfBusiness: (selectedLinesOfBusiness: DropdownItem<BenchmarkingLineOfBusinessEnum>[]) => void;
  setSelectedStates: (selectedStates: DropdownItem<string>[]) => void;
  stateOptions: DropdownItem<string>[];
}

const initialValues: CompetitiveInsightsManageContextValues = {
  activeOptions: [],
  clientOptions: [],
  displayedFilters: {
    selectedActive: [],
    selectedClients: [],
    selectedLinesOfBusiness: [],
    selectedStates: [],
  },
  filteredProjects: [],
  isQuestInternal: false,
  lineOfBusinessOptions: [],
  onApplyFiltersClick: () => null,
  onClearCompetitiveInsightsManageFiltersClick: () => null,
  projects: [],
  projectsLoading: false,
  refetchProjects: () => Promise.resolve(),
  selectedActive: [],
  selectedClients: [],
  selectedLinesOfBusiness: [],
  selectedStates: [],
  setDisplayedFilters: () => null,
  setSelectedActive: () => null,
  setSelectedClients: () => null,
  setSelectedLinesOfBusiness: () => null,
  setSelectedStates: () => null,
  stateOptions: [],
};

export const CompetitiveInsightsManageContext = createContext(initialValues);

interface CompetitiveInsightsManageContextProviderProps {
  children: React.ReactNode;
}

const CompetitiveInsightsManageContextProvider: FC<CompetitiveInsightsManageContextProviderProps> = (props: CompetitiveInsightsManageContextProviderProps) => {
  const activeOptions: DropdownItem<CompetitiveInsightsManageProjectStatusEnum>[] = Object.values(CompetitiveInsightsManageProjectStatusEnum).map(x => {
    return { key: x, name: x };
  });

  const lineOfBusinessOptions: DropdownItem<BenchmarkingLineOfBusinessEnum>[] = Object.values(BenchmarkingLineOfBusinessEnum)
    .filter(x => typeof x === "number")
    .map(x => {
      return { key: x as BenchmarkingLineOfBusinessEnum, name: getLineOfBusinessDisplayName(x as BenchmarkingLineOfBusinessEnum) };
    });

  const [filteredProjects, setFilteredProjects] = useState<CompetitiveInsightsSupersetProjectConfigurations[]>([]);
  const [stateOptions, setStateOptions] = useState<DropdownItem<string>[]>([]);
  const [clientOptions, setClientOptions] = useState<DropdownItem<string>[]>([]);
  const [displayedFilters, setDisplayedFilters] = useState<CompetitiveInsightsManageDisplayFilters>(initialValues.displayedFilters);
  const [selectedActive, setSelectedActive] = useState<DropdownItem<CompetitiveInsightsManageProjectStatusEnum>[]>(activeOptions);
  const [selectedLinesOfBusiness, setSelectedLinesOfBusiness] = useState<DropdownItem<BenchmarkingLineOfBusinessEnum>[]>(lineOfBusinessOptions);
  const [selectedStates, setSelectedStates] = useState<DropdownItem<string>[]>([]);
  const [selectedClients, setSelectedClients] = useState<DropdownItem<string>[]>([]);
  const userContext = useContext(UserContext);
  const isQuestInternal = userContext?.clientName === "Quest Analytics";

  const { isLoading: projectsLoading, projects: competitiveInsightsManageProjects, refetchProjects } = useCompetitiveInsightsManageProjects(userContext?.userId);

  const projects = competitiveInsightsManageProjects ?? [];

  useEffect(() => {
    if (!competitiveInsightsManageProjects) return;

    setFilteredProjects(competitiveInsightsManageProjects);

    const uniqueStates = Array.from(new Set(competitiveInsightsManageProjects.map(x => x.stateAbbreviation ?? ""))).sort();
    const newStateOptions = uniqueStates.map(x => { return { key: x, name: x }; });
    setStateOptions(newStateOptions);
    setSelectedStates(newStateOptions);

    const uniqueClients = Array.from(new Set(competitiveInsightsManageProjects.map(x => x.clientName))).sort();
    const newClientOptions = uniqueClients.map(x => { return { key: x, name: x }; });
    setClientOptions(newClientOptions);
    setSelectedClients(newClientOptions);

    setDisplayedFilters({
      selectedActive: activeOptions.map(x => x.key),
      selectedClients: newClientOptions.map(x => x.key),
      selectedLinesOfBusiness: lineOfBusinessOptions.map(x => x.key),
      selectedStates: newStateOptions.map(x => x.key),
    });
  }, [competitiveInsightsManageProjects]);

  const onApplyFiltersClick = (): void => {
    const newDisplayedFilters: CompetitiveInsightsManageDisplayFilters = {
      selectedActive: selectedActive.map(x => x.key),
      selectedClients: selectedClients.map(x => x.key),
      selectedLinesOfBusiness: selectedLinesOfBusiness.map(x => x.key),
      selectedStates: selectedStates.map(x => x.key),
    };

    setDisplayedFilters(newDisplayedFilters);

    const selectedLinesOfBusinessDisplayNames = newDisplayedFilters.selectedLinesOfBusiness.map(getLineOfBusinessDisplayName);

    const newFilteredProjects = projects.filter(x =>
      newDisplayedFilters.selectedActive.includes(x.isActive ? CompetitiveInsightsManageProjectStatusEnum.Active : CompetitiveInsightsManageProjectStatusEnum.Inactive) &&
      selectedLinesOfBusinessDisplayNames.includes(getLineOfBusinessDisplayName(x.lineOfBusiness)) &&
      newDisplayedFilters.selectedStates.includes(x.stateAbbreviation ?? "") &&
      newDisplayedFilters.selectedClients.includes(x.clientName)
    );

    setFilteredProjects(newFilteredProjects);
  }

  const onClearCompetitiveInsightsManageFiltersClick = (): void => {
    setSelectedActive(activeOptions);
    setSelectedLinesOfBusiness(lineOfBusinessOptions);
    setSelectedStates(stateOptions);
    setSelectedClients(clientOptions);
  }

  return (
    <CompetitiveInsightsManageContext.Provider
      value={{
        activeOptions,
        clientOptions,
        displayedFilters,
        filteredProjects,
        isQuestInternal,
        lineOfBusinessOptions,
        onApplyFiltersClick,
        onClearCompetitiveInsightsManageFiltersClick,
        projects,
        projectsLoading,
        refetchProjects,
        selectedActive,
        selectedClients,
        selectedLinesOfBusiness,
        selectedStates,
        setDisplayedFilters,
        setSelectedActive,
        setSelectedClients,
        setSelectedLinesOfBusiness,
        setSelectedStates,
        stateOptions,
      }}>
      {props.children}
    </CompetitiveInsightsManageContext.Provider>
  );
}

export default CompetitiveInsightsManageContextProvider;
